import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputText from "../../../components/forms/InputText.jsx";
import { apiUrl } from "../../../config/config.js";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

function RoomsDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const type = location.state?.type || "";
	const data = useMemo(() => location.state?.data || [], [location.state?.data]);
	const [itemId, setItemId] = useState("");
	const [roomNumber, setRoomNumber] = useState("");
	const [roomType, setRoomType] = useState("");
	const [capacity, setCapacity] = useState("");
	const [price, setPrice] = useState("");
	const [description, setDescription] = useState("");
	const [status, setStatus] = useState("");
	const [bedType, setBedType] = useState("");
	const [imageUrl, setImageUrl] = useState("");
	const [amenities, setAmenities] = useState("");
	const [floor, setFloor] = useState("");
	const [hasBalcony, setHasBalcony] = useState(false);
	const [hasOceanView, setHasOceanView] = useState(false);

	const { handleDelete, handleSubmit } = useHandleActionItem(data, "rooms");

	useEffect(() => {
		if (type === "edit" && data) {
			setItemId(data.ID || "");
			setRoomNumber(data["Número de Habitación"] || "");
			setRoomType(data.Tipo || "");
			setCapacity(data.Capacidad || "");
			setPrice(data.Precio || "");
			setDescription(data.Descripción || "");
			setStatus(data.Estado || "");
			setBedType(data["Tipo de Cama"] || "");
			setImageUrl(data["URL de Imagen"] || "");
			setAmenities(data.Comodidades ? data.Comodidades.split(",") : []);
			setFloor(data.Piso || "");
			setHasBalcony(data.Balcón || false);
			setHasOceanView(data["Vista al Mar"] || false);
		}
	}, [type, data]);

	const validateForm = () => {
		if (!roomNumber || !roomType || !capacity || !price || !description || !status || !bedType) {
			Toast.fire({
				icon: "error",
				title: "Todos los espacios son requeridos",
				background: "#1e2836",
			});
			return false;
		}
		return true;
	};

	const handleDeleteForm = async (event, itemId) => {
		event.preventDefault();
		if (!validateForm()) return;
		await handleDelete(itemId, "rooms");
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;
		await handleSubmit(type === "add" ? "add" : "update", "rooms", {
			id: itemId,
			roomNumber,
			type: roomType,
			capacity,
			price,
			description,
			status,
			bedType,
			imageUrl,
			amenities: amenities.join(","),
			floor,
			hasBalcony,
			hasOceanView,
		});
		navigate("/rooms");
	};

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{type === "add" ? "Agregar Habitación" : "Actualizar Habitación"}</h1>
				<InputText label="Número de Habitación" placeholder="Número de Habitación" value={roomNumber} setValue={setRoomNumber} />
				<InputText label="Tipo" placeholder="Tipo" value={roomType} setValue={setRoomType} />
				<InputText label="Capacidad" placeholder="Capacidad" value={capacity} setValue={setCapacity} />
				<InputText label="Precio" placeholder="Precio" value={price} setValue={setPrice} />
				<InputText label="Descripción" placeholder="Descripción" value={description} setValue={setDescription} />

				<label className="form-label">Tipo de Cama</label>
				<select className="form-select mb-3" value={bedType} onChange={(e) => setBedType(e.target.value)}>
					<option value="">Seleccione un tipo de cama</option>
					<option value="individual">Individual</option>
					<option value="matrimonial">Matrimonial</option>
					<option value="queen">Queen</option>
					<option value="king">King</option>
					<option value="doble">Doble</option>
				</select>
				<InputText label="URL de Imagen" placeholder="URL de Imagen" value={imageUrl} setValue={setImageUrl} />
				<div className="mb-3">
					<label className="form-label">Comodidades</label>
					<div className="d-flex flex-wrap gap-3">
						{[
							"TV",
							"WiFi",
							"A/C",
							"Jacuzzi",
							"Mini Bar",
							"Caja Fuerte",
							"Servicio a la Habitación",
							"Cafetera",
							"Secador de Pelo",
							"Plancha",
							"Despertador",
							"Teléfono",
							"Escritorio",
							"Kit de Baño",
							"Bata de Baño",
						].map((amenity) => (
							<div className="form-check" key={amenity}>
								<input
									type="checkbox"
									className="form-check-input"
									checked={amenities.includes(amenity)}
									onChange={(e) => {
										if (e.target.checked) {
											setAmenities([...amenities, amenity]);
										} else {
											setAmenities(amenities.filter((item) => item !== amenity));
										}
									}}
								/>
								<label className="form-check-label">{amenity}</label>
							</div>
						))}
					</div>
				</div>
				<InputText label="Piso" placeholder="Piso" value={floor} setValue={setFloor} />
				<div className="mb-3">
					<label className="form-label">Balcón</label>
					<input type="checkbox" checked={hasBalcony} onChange={(e) => setHasBalcony(e.target.checked)} />
				</div>
				<div className="mb-3">
					<label className="form-label">Vista al Mar</label>
					<input type="checkbox" checked={hasOceanView} onChange={(e) => setHasOceanView(e.target.checked)} />
				</div>
				<div className="mb-3">
					<label className="form-label">Estado</label>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={status === "available"}
							onChange={(e) => setStatus(e.target.checked ? "available" : "unavailable")}
						/>
						<label className="form-check-label">{status === "available" ? "Disponible" : "No Disponible"}</label>
					</div>
				</div>

				<div className="mb-3 d-flex justify-content-around ">
					<button type="submit" className="btn btn-outline-theme btn-lg">
						{type === "add" ? "Agregar" : "Actualizar"}
					</button>
					<button onClick={() => navigate("/rooms")} className="btn btn-outline-theme btn-lg">
						Atrás
					</button>
					<button
						onClick={(event) => {
							handleDeleteForm(event, itemId);
						}}
						className="btn btn-outline-danger btn-lg">
						Eliminar
					</button>
				</div>
			</form>
		</div>
	);
}

export default RoomsDetails;
